import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, XCircle, CreditCard, ShieldCheck, CheckCircle2, Loader2, MessageCircle, Send } from "lucide-react";

export default function ClientTradelineReservations() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const pollTimer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/client/tradeline-reservations");
      setItems(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  // Handle post-checkout return: `?session_id=...&paid=1`
  useEffect(() => {
    const sid = params.get("session_id");
    const paid = params.get("paid");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      toast.error("Payment cancelled — reservation still pending.");
      setParams({}, { replace: true });
      return;
    }
    if (!sid || !paid) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Payment received — your tradeline reservation is confirmed!");
          setParams({}, { replace: true });
          await load();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          toast.error("Payment did not complete. Please try again.");
          setParams({}, { replace: true });
          return;
        }
      } catch (e) { /* transient */ }
      if (attempts < 8) {
        pollTimer.current = setTimeout(poll, 2000);
      } else {
        toast.info("Still confirming your payment — refresh in a moment.");
        setParams({}, { replace: true });
      }
    };
    poll();
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this tradeline reservation?")) return;
    try {
      await api.post(`/client/tradeline-reservations/${id}/cancel`);
      toast.success("Cancelled");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    }
  };

  const pay = async (r) => {
    setPayingId(r.id);
    try {
      const { data } = await api.post(`/tradelines/reservations/${r.id}/checkout`, {
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout — please try again.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
    } finally {
      setPayingId(null);
    }
  };

  return (
    <AppShell>
      <div className="cq-in max-w-4xl">
        <button onClick={() => nav("/portal/tradelines")} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-slate-500 hover:text-slate-800 mb-2">
          <ArrowLeft size={14} /> Back to marketplace
        </button>
        <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Client portal</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">My Tradeline Reservations</h1>
        <p className="text-slate-600 mt-1">Pay now to lock in your line — non-refundable once processed. We'll follow up with next steps within one business day.</p>

        <div className="mt-6 space-y-3">
          {items.length === 0 && <Card className="p-8 rounded-2xl text-center text-sm text-slate-400 border-slate-100">No reservations yet.</Card>}
          {items.map((r) => {
            const isPaid = r.status === "Paid" || r.payment_status === "paid";
            const isCancelled = r.status === "Cancelled";
            return (
              <Card key={r.id} className="p-5 rounded-2xl border-slate-100" data-testid={`tl-resv-${r.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <span className={`text-[10px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${
                      isPaid ? "bg-emerald-100 text-emerald-800"
                      : isCancelled ? "bg-slate-200 text-slate-600"
                      : "bg-amber-100 text-amber-800"
                    }`}>
                      {isPaid && <CheckCircle2 size={10} />}
                      {r.status}
                    </span>
                    {r.promo_code && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">
                        {r.promo_label || r.promo_code}
                      </span>
                    )}
                    <p className="font-display font-black text-lg text-slate-900 mt-1">
                      {(r.line_items || []).map((i) => `${i.bank} $${i.credit_limit.toLocaleString()}`).join(" · ")}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      Total: <b>${r.total_price}</b>
                      {r.discount_amount ? <span className="text-emerald-700 font-bold"> (saved ${r.discount_amount})</span> : null}
                      {" · "}Requested {new Date(r.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {!isPaid && !isCancelled && (
                      <>
                        <Button
                          onClick={() => pay(r)}
                          disabled={payingId === r.id}
                          className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
                          data-testid={`tl-pay-${r.id}`}
                        >
                          {payingId === r.id ? <Loader2 size={13} className="mr-1 animate-spin" /> : <CreditCard size={13} className="mr-1" />}
                          Pay ${r.total_price}
                        </Button>
                        <Button variant="outline" onClick={() => cancel(r.id)} className="border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`tl-cancel-${r.id}`}>
                          <XCircle size={13} className="mr-1" /> Cancel
                        </Button>
                      </>
                    )}
                    {isPaid && (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-bold">
                        <CheckCircle2 size={14} /> Paid {r.paid_at ? new Date(r.paid_at).toLocaleDateString() : ""}
                      </span>
                    )}
                  </div>
                </div>
                <ReservationChat reservationId={r.id} />
              </Card>
            );
          })}
        </div>

        <Card className="mt-8 p-5 rounded-2xl border-slate-100 bg-slate-50 flex items-start gap-3">
          <ShieldCheck size={18} className="text-emerald-600 mt-0.5" />
          <p className="text-xs text-slate-600">
            Payments are processed securely by <b>Stripe</b>. We never see or store your card number.
            Once payment clears, the reservation is <b>non-refundable</b> — please review your cart before checkout.
          </p>
        </Card>
      </div>
    </AppShell>
  );
}

function ReservationChat({ reservationId }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/client/tradeline-reservations/${reservationId}/messages`);
      setMessages(data?.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [open, reservationId]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/client/tradeline-reservations/${reservationId}/messages`, { text: t });
      setMessages((prev) => [...prev, data]);
      setText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`tl-chat-${reservationId}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 hover:text-amber-800"
        data-testid={`tl-chat-toggle-${reservationId}`}
      >
        <MessageCircle size={14} /> {open ? "Hide" : "Open"} tradeline chat
        {messages.length > 0 && !open && <span className="ml-1 text-slate-400">({messages.length})</span>}
      </button>
      {open && (
        <div className="mt-3 rounded-xl border border-slate-200 p-3 flex flex-col gap-2">
          <div className="max-h-64 min-h-[80px] overflow-y-auto flex flex-col gap-2 bg-slate-50 rounded-lg p-2">
            {messages.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No messages yet. Your tradeline rep will reach out about next steps.</p>}
            {messages.map((m) => {
              const mine = m.sender_role === "client";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${mine ? "bg-sky-500 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                    <p className="text-[10px] font-bold opacity-75 mb-0.5">{m.sender_name}</p>
                    <p className="whitespace-pre-wrap">{m.text}</p>
                    <p className="text-[9px] opacity-60 mt-0.5">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Type a message to your tradeline rep…" data-testid={`tl-chat-input-${reservationId}`} />
            <Button onClick={send} disabled={busy || !text.trim()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`tl-chat-send-${reservationId}`}>
              <Send size={14} className="mr-1" /> Send
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
